// ============================================
// GUIDE PAGE - How to Use Alpha Options
// Public page with 10 sections
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  ArrowLeft, BookOpen, Search, BarChart3, Target, Rocket,
  Shield, TrendingUp, Clock, DollarSign, LayoutDashboard
} from 'lucide-react';

const sections = [
  { id: 'getting-started', title: '1. Getting Started', icon: <BookOpen size={20} /> },
  { id: 'scanning', title: '2. Scanning a Ticker', icon: <Search size={20} /> },
  { id: 'understanding-score', title: '3. Understanding Your Score', icon: <BarChart3 size={20} /> },
  { id: 'trade-plan', title: '4. Reading Your Trade Plan', icon: <Target size={20} /> },
  { id: 'moonshot', title: '5. Using Moonshot AI', icon: <Rocket size={20} /> },
  { id: 'why-moonshot', title: '6. Why Moonshot Matters', icon: <Shield size={20} /> },
  { id: 'managing-trades', title: '7. Managing Open Trades', icon: <TrendingUp size={20} /> },
  { id: 'when-to-close', title: '8. When to Close Positions', icon: <Clock size={20} /> },
  { id: 'taking-profit', title: '9. When to Take Profit', icon: <DollarSign size={20} /> },
  { id: 'dashboard', title: '10. Dashboard & Stats', icon: <LayoutDashboard size={20} /> },
];

export const GuidePage: React.FC = () => {
  const { isSignedIn } = useUser();

  return (
    <div className="min-h-screen bg-terminal-bg text-terminal-text font-mono">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-terminal-dim hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                7S
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                How to Use <span className="text-cyan-400">Alpha Options</span>
              </span>
            </div>
            <Link
              to={isSignedIn ? '/scanner' : '/signup'}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold text-sm hover:opacity-90"
            >
              {isSignedIn ? 'Open Scanner' : 'Get Started'}
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-terminal-card rounded-xl border border-terminal-border">
          <h2 className="text-lg font-bold text-terminal-bright mb-4">Contents</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-terminal-dim hover:text-cyan-400 hover:bg-cyan-500/5 transition-colors text-sm"
              >
                <span className="text-cyan-400">{s.icon}</span>
                {s.title}
              </a>
            ))}
          </div>
        </nav>

        {/* Sections */}
        <div className="space-y-16">

          {/* 1. Getting Started */}
          <section id="getting-started">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><BookOpen size={24} /></span>
              1. Getting Started
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Alpha Options is an options trading analysis platform that uses a proprietary <strong className="text-terminal-bright">7-Step Scoring Framework</strong> to evaluate trade setups.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">How to Sign Up</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Click <strong className="text-cyan-400">Get Started</strong> on the homepage</li>
                  <li>Create your account with email or Google</li>
                  <li>Choose your plan or start a <strong className="text-emerald-400">7-day free trial</strong></li>
                  <li>Head to the Scanner and enter your first ticker</li>
                </ol>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Plans</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="font-bold text-white">Free Trial</div>
                    <div className="text-terminal-dim">5 scans/day for 7 days</div>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                    <div className="font-bold text-cyan-400">Basic &mdash; &pound;19.99/mo</div>
                    <div className="text-terminal-dim">5 scans/day + Moonshot AI</div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="font-bold text-amber-400">Elite &mdash; &pound;29.99/mo</div>
                    <div className="text-terminal-dim">50 scans/day + full access</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Scanning a Ticker */}
          <section id="scanning">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><Search size={24} /></span>
              2. Scanning a Ticker
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>The Scanner is where the magic happens. Enter any US stock ticker and our engine runs the full 8-step analysis in seconds.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">How to Scan</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Open the <strong className="text-cyan-400">Scanner</strong> from the dashboard or nav</li>
                  <li>Type a ticker symbol (e.g., <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">AAPL</code>, <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">SPY</code>, <code className="px-1.5 py-0.5 bg-slate-800 rounded text-cyan-400">TSLA</code>)</li>
                  <li>Click <strong className="text-emerald-400">ANALYZE</strong> or press Enter</li>
                  <li>Wait for the analysis (fetches real-time market data, technicals, options chain, and news)</li>
                  <li>Review your score, trade plan, and strategy recommendation</li>
                </ol>
              </div>

              <p className="text-sm text-terminal-dim">You can also click any of the <strong>Popular Tickers</strong> below the search box for a quick scan.</p>
            </div>
          </section>

          {/* 3. Understanding Your Score */}
          <section id="understanding-score">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><BarChart3 size={24} /></span>
              3. Understanding Your Score
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Every scan produces a score from <strong className="text-terminal-bright">0 to 100</strong>, based on 8 weighted steps totalling 145 raw points, normalised to a 100-point scale.</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <div className="text-3xl font-black text-emerald-400">70+</div>
                  <div className="font-bold text-emerald-400 mt-1">TRADE</div>
                  <div className="text-sm text-terminal-dim mt-1">Proceed with the setup</div>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
                  <div className="text-3xl font-black text-amber-400">50&ndash;69</div>
                  <div className="font-bold text-amber-400 mt-1">CAUTION</div>
                  <div className="text-sm text-terminal-dim mt-1">Additional review needed</div>
                </div>
                <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-center">
                  <div className="text-3xl font-black text-rose-400">&lt;50</div>
                  <div className="font-bold text-rose-400 mt-1">NO TRADE</div>
                  <div className="text-sm text-terminal-dim mt-1">Skip this setup</div>
                </div>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">The 8 Steps</h3>
                <div className="space-y-2 text-sm">
                  {[
                    { step: '1. Sentiment & Context', pts: 15, desc: 'VIX level, sector strength, price momentum' },
                    { step: '2. Technical Analysis', pts: 20, desc: 'RSI, MACD, EMA alignment, squeeze detection' },
                    { step: '3. Options Liquidity', pts: 15, desc: 'Bid/ask spreads, open interest, volume' },
                    { step: '4. Risk Assessment', pts: 25, desc: 'Earnings proximity, FOMC dates, IV rank' },
                    { step: '5. Greeks Analysis', pts: 20, desc: 'IV environment, delta alignment' },
                    { step: '6. Entry Criteria', pts: 25, desc: '52-week position, volume, support/resistance' },
                    { step: '7. Trade Review', pts: 10, desc: 'Signal conflict detection' },
                    { step: '8. Position Sizing', pts: 15, desc: 'VIX-adjusted sizing recommendations' },
                  ].map((s) => (
                    <div key={s.step} className="flex items-start gap-3 py-1.5 border-b border-slate-800/50 last:border-0">
                      <span className="text-cyan-400 font-bold whitespace-nowrap">{s.step}</span>
                      <span className="text-terminal-dim flex-1">{s.desc}</span>
                      <span className="text-terminal-bright font-bold whitespace-nowrap">{s.pts} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 4. Reading Your Trade Plan */}
          <section id="trade-plan">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><Target size={24} /></span>
              4. Reading Your Trade Plan
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>When a ticker scores <strong className="text-emerald-400">TRADE</strong> or <strong className="text-amber-400">CAUTION</strong>, you get a full trade plan with strategy details.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4 space-y-3 text-sm">
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Strategy</span>
                  <span className="text-terminal-bright font-bold">e.g., Bull Call Spread</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Direction</span>
                  <span className="text-emerald-400 font-bold">BULLISH / BEARISH / NEUTRAL</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Trade Legs</span>
                  <span className="text-terminal-bright">Buy/Sell strikes, expiry, DTE</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Max Profit</span>
                  <span className="text-emerald-400 font-bold">Your best-case return</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Max Loss</span>
                  <span className="text-rose-400 font-bold">Your worst-case risk (defined)</span>
                </div>
                <div className="flex justify-between border-b border-slate-800/50 pb-2">
                  <span className="text-terminal-dim">Breakeven</span>
                  <span className="text-terminal-bright">Price where you neither gain nor lose</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-terminal-dim">Win Probability</span>
                  <span className="text-cyan-400 font-bold">Estimated chance of profit</span>
                </div>
              </div>

              <p className="text-sm text-terminal-dim">All strategies are <strong className="text-terminal-bright">defined-risk</strong> &mdash; you always know your maximum possible loss before entering.</p>
            </div>
          </section>

          {/* 5. Using Moonshot AI */}
          <section id="moonshot">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-purple-400"><Rocket size={24} /></span>
              5. Using Moonshot AI
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p><strong className="text-purple-400">Moonshot AI</strong> is your personal trade advisor. It uses the same 7-Step Framework as the scanner but adds <strong className="text-terminal-bright">real-time web search</strong> for the latest data.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">How to Access Moonshot</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">1.</span>
                    <span><strong className="text-terminal-bright">From the Scanner:</strong> After scanning a ticker, click the <strong className="text-purple-400">Ask Moonshot</strong> button on the score card. This sends your full analysis to Moonshot for deeper insight.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">2.</span>
                    <span><strong className="text-terminal-bright">Directly:</strong> Go to the Moonshot page from the dashboard. You can ask any trading question or upload a screenshot of your broker positions.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">What You Can Ask</h3>
                <ul className="space-y-1 text-sm text-terminal-dim">
                  <li>&bull; &ldquo;Analyse my AAPL trade &mdash; is this a good entry?&rdquo;</li>
                  <li>&bull; &ldquo;Help me manage my open position on TSLA&rdquo;</li>
                  <li>&bull; &ldquo;Find me options opportunities this week&rdquo;</li>
                  <li>&bull; &ldquo;Should I take profits or hold?&rdquo;</li>
                  <li>&bull; Upload a broker screenshot for instant analysis</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 6. Why Moonshot Matters */}
          <section id="why-moonshot">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><Shield size={24} /></span>
              6. Why Moonshot Matters
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>The Scanner and Moonshot complement each other. Here&rsquo;s why you should use both:</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                  <h3 className="font-bold text-cyan-400 mb-2">Scanner</h3>
                  <ul className="space-y-1 text-sm text-terminal-dim">
                    <li>&bull; Quantitative scoring (0-100)</li>
                    <li>&bull; Consistent, repeatable analysis</li>
                    <li>&bull; Market data snapshot at scan time</li>
                    <li>&bull; Automated strategy selection</li>
                  </ul>
                </div>
                <div className="bg-terminal-card rounded-lg border border-purple-500/30 p-4">
                  <h3 className="font-bold text-purple-400 mb-2">Moonshot AI</h3>
                  <ul className="space-y-1 text-sm text-terminal-dim">
                    <li>&bull; Real-time web search for latest data</li>
                    <li>&bull; Checks if conditions changed since scan</li>
                    <li>&bull; Finds news/catalysts scanner may miss</li>
                    <li>&bull; Conversational follow-up questions</li>
                  </ul>
                </div>
              </div>

              <p className="text-sm text-terminal-dim">Moonshot can validate or challenge the scanner&rsquo;s score. If the market moved since your scan, Moonshot will catch it and update you.</p>
            </div>
          </section>

          {/* 7. Managing Open Trades */}
          <section id="managing-trades">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><TrendingUp size={24} /></span>
              7. Managing Open Trades
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Once you&rsquo;re in a trade, Moonshot becomes your trade management advisor. Ask it about your existing positions for a clear <strong className="text-terminal-bright">IF-THEN decision matrix</strong>.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Example Questions for Open Trades</h3>
                <ul className="space-y-2 text-sm text-terminal-dim">
                  <li>&bull; &ldquo;I have 5 contracts of AAPL $180 calls, bought at $4.50, now worth $6.20. Should I hold or take profits?&rdquo;</li>
                  <li>&bull; &ldquo;My TSLA $250 puts are down 60% with 10 DTE. What should I do?&rdquo;</li>
                  <li>&bull; &ldquo;Give me a trade management plan for my SPY iron condor expiring next Friday&rdquo;</li>
                </ul>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">What Moonshot Will Give You</h3>
                <ul className="space-y-1 text-sm text-terminal-dim">
                  <li>&bull; Clear <strong className="text-terminal-bright">HOLD / TRIM / CLOSE</strong> verdict</li>
                  <li>&bull; Profit targets and stop-loss levels</li>
                  <li>&bull; Adjustment triggers (when to roll, hedge, or exit)</li>
                  <li>&bull; Action table: exact contracts, targets, and reasoning</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 8. When to Close Positions */}
          <section id="when-to-close">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><Clock size={24} /></span>
              8. When to Close Positions
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Alpha Options follows strict <strong className="text-terminal-bright">DTE (Days to Expiration)</strong> rules and an earnings safety rule.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">DTE Rules</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-emerald-400 font-bold w-20">45-30 DTE</span>
                    <span className="text-terminal-dim">Ideal entry zone</span>
                  </div>
                  <div className="flex items-center gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-amber-400 font-bold w-20">21-14 DTE</span>
                    <span className="text-terminal-dim">Reassess your thesis &mdash; theta is accelerating</span>
                  </div>
                  <div className="flex items-center gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-rose-400 font-bold w-20">14-7 DTE</span>
                    <span className="text-terminal-dim">Close if not profitable</span>
                  </div>
                  <div className="flex items-center gap-3 py-1.5">
                    <span className="text-rose-500 font-bold w-20">7-0 DTE</span>
                    <span className="text-rose-400 font-bold">Close 100% &mdash; gamma risk is extreme</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-500/10 rounded-lg border border-amber-500/20 p-4">
                <h3 className="font-bold text-amber-400 mb-2">Earnings Rule</h3>
                <p className="text-sm text-terminal-dim">
                  <strong className="text-terminal-bright">Close 2 trading days BEFORE earnings. No exceptions.</strong> Earnings are binary events with unpredictable gaps. Holding options through earnings is gambling, not trading.
                </p>
              </div>
            </div>
          </section>

          {/* 9. When to Take Profit */}
          <section id="taking-profit">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><DollarSign size={24} /></span>
              9. When to Take Profit
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Alpha Options uses a <strong className="text-terminal-bright">tiered exit strategy</strong> to lock in gains while letting winners run.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Credit Spreads</h3>
                <p className="text-sm text-terminal-dim">Close at <strong className="text-emerald-400">50% of max profit</strong>. This is the non-negotiable rule. Holding for the last 50% exposes you to far more risk than the remaining reward justifies.</p>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Debit Spreads / Long Options</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-emerald-400 font-bold w-28">At 30% profit</span>
                    <span className="text-terminal-dim">Trim 30% of position</span>
                  </div>
                  <div className="flex items-center gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-emerald-400 font-bold w-28">At 50% profit</span>
                    <span className="text-terminal-dim">Trim another 30%, move stop to breakeven</span>
                  </div>
                  <div className="flex items-center gap-3 py-1.5">
                    <span className="text-emerald-400 font-bold w-28">Remainder</span>
                    <span className="text-terminal-dim">Trail with tightening stop (1.0x ATR &rarr; 0.75x ATR)</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-terminal-dim"><strong className="text-terminal-bright">Trailing stops:</strong> At 25% profit, tighten your stop to 1.0x ATR. At 50% profit, tighten to 0.75x ATR. This protects gains while giving the trade room to run.</p>
            </div>
          </section>

          {/* 10. Dashboard & Stats */}
          <section id="dashboard">
            <h2 className="text-2xl font-bold text-terminal-bright mb-6 flex items-center gap-3">
              <span className="text-cyan-400"><LayoutDashboard size={24} /></span>
              10. Dashboard & Stats
            </h2>
            <div className="space-y-4 text-terminal-text leading-relaxed">
              <p>Your dashboard gives you a quick overview of your account, scan activity, and watchlist.</p>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Dashboard Cards</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-cyan-400 font-bold whitespace-nowrap">Current Plan</span>
                    <span className="text-terminal-dim">Shows your subscription tier (Free, Trial, Basic, Elite, Promo, Admin)</span>
                  </div>
                  <div className="flex items-start gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-cyan-400 font-bold whitespace-nowrap">Scans Today</span>
                    <span className="text-terminal-dim">How many scans you&rsquo;ve used out of your daily limit. Resets at midnight UTC.</span>
                  </div>
                  <div className="flex items-start gap-3 py-1.5 border-b border-slate-800/50">
                    <span className="text-cyan-400 font-bold whitespace-nowrap">Remaining</span>
                    <span className="text-terminal-dim">Scans left for today</span>
                  </div>
                  <div className="flex items-start gap-3 py-1.5">
                    <span className="text-cyan-400 font-bold whitespace-nowrap">Watchlist</span>
                    <span className="text-terminal-dim">Tickers you&rsquo;re tracking. Click &ldquo;Scan&rdquo; next to any ticker for a quick analysis.</span>
                  </div>
                </div>
              </div>

              <div className="bg-terminal-card rounded-lg border border-terminal-border p-4">
                <h3 className="font-bold text-terminal-bright mb-3">Quick Actions</h3>
                <ul className="space-y-1 text-sm text-terminal-dim">
                  <li>&bull; <strong className="text-terminal-bright">Open Scanner</strong> &mdash; Jump straight to scanning</li>
                  <li>&bull; <strong className="text-terminal-bright">My Positions</strong> &mdash; Track your open trades and set alerts</li>
                  <li>&bull; <strong className="text-terminal-bright">Ask Moonshot</strong> &mdash; Get AI-powered trade advice</li>
                  <li>&bull; <strong className="text-terminal-bright">View Plans</strong> &mdash; Upgrade or manage your subscription</li>
                </ul>
              </div>
            </div>
          </section>

        </div>

        {/* Bottom CTA */}
        <div className="mt-16 mb-8 text-center">
          <p className="text-terminal-dim mb-4">Ready to start trading smarter?</p>
          <Link
            to={isSignedIn ? '/scanner' : '/signup'}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-xl font-bold text-lg hover:opacity-90 transition-opacity"
          >
            {isSignedIn ? 'Open Scanner' : 'Start Free Trial'}
          </Link>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-terminal-border pt-6 pb-8">
          <p className="text-xs text-terminal-dim text-center leading-relaxed max-w-2xl mx-auto">
            Alpha Options provides educational analysis only and does not constitute financial advice.
            Options and derivatives involve significant risk and can result in the loss of your entire investment.
            Always do your own research and consult a qualified financial adviser before making investment decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuidePage;
